import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from '@app/store';
import {
  createAffiliate,
  updateAffiliate,
  fetchAffiliateById,
  AffiliateActions,
} from "./AffiliateSlice";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { Box, Typography } from "@mui/material";
import {
  AffiliateFieldData,
  AffiliateOutputDTO,
  LOGO_UPLOAD_CONFIG,
  BACKGROUND_UPLOAD_CONFIG,
  UploadConfig,
} from './types';
import { ApiError } from '@/types/api';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { AffiliateMapper } from "./AffiliateMapper";
import { useNotification } from '@hooks/useNotification';

import styled from "styled-components";

const BoxContainer = styled.div`
  flex-shrink: 0; /* Não deixa o Box crescer */
  margin-right: 8px; /* Espaçamento entre os itens */
`;

const LogoPreviewContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 16px;
`;

const LogoPreview = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px;
`;

const validateImageFile = (
  file: File,
  type: UploadConfig
): Promise<string | null> => {
  return new Promise((resolve) => {
    if (file.size > type.MAX_FILE_SIZE) {
      resolve("O arquivo deve ter no máximo " + type.MAX_FILE_SIZE + " bytes");
      return;
    }

    if (!type.ACCEPTED_TYPES.includes(file.type)) {
      resolve(
        "Formato de arquivo inválido. Use " +
          type.ACCEPTED_TYPES.join(", ") +
          "."
      );
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (
        img.width > type.MAX_DIMENSIONS.width ||
        img.height > type.MAX_DIMENSIONS.height
      ) {
        resolve(
          `A imagem deve ter no máximo ${type.MAX_DIMENSIONS.width}x${type.MAX_DIMENSIONS.height} pixels`
        );
      } else {
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve("Erro ao carregar a imagem");
    };

    img.src = objectUrl;
  });
};

const AffiliateForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.affiliate
  );
  const [formData, setFormData] = useState<Partial<AffiliateOutputDTO>>({});
  const [localFields, setLocalFields] = useState<AffiliateFieldData[]>([]);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | undefined>(undefined);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const [selectedBackground, setSelectedBackground] = useState<File | null>(
    null
  );
  const [backgroundError, setBackgroundError] = useState<string | undefined>(
    undefined
  );
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState<
    string | null
  >(null);

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do Affiliate:", id);
        isLoadingRef.current = true;

        if (id) {
          console.log("📡 PhaseFormForm - Carregando Affiliate:", id);
          const resultAction = await dispatch(fetchAffiliateById(id));

          if (fetchAffiliateById.fulfilled.match(resultAction)) {
            console.log("✅ Affiliate carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Affiliate:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(AffiliateActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 Affiliate - Atualizando dados do formulário:",
        currentItem,
        currentItem.logoUrl,
        currentItem.backgroundUrl
      );

      setFormData({
        name: currentItem.name,
        email: currentItem.email,
        logoUrl: currentItem.logoUrl || undefined,
        backgroundUrl: currentItem.backgroundUrl || undefined,
      });
    }
  }, [currentItem]);

  const handleLogoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = await validateImageFile(file, LOGO_UPLOAD_CONFIG);
    if (error) {
      setLogoError(error);
      setSelectedLogo(null);
      setLogoPreviewUrl(null);
      setBackgroundPreviewUrl(null);
      setBackgroundError(undefined);
      setSelectedBackground(null);

      notification.showError(error);
    } else {
      setLogoError(undefined);
      setSelectedLogo(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleBackgroundChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = await validateImageFile(file, BACKGROUND_UPLOAD_CONFIG);
    if (error) {
      setBackgroundError(error);
      setSelectedBackground(null);
      setBackgroundPreviewUrl(null);
      notification.showError(error);
    } else {
      setBackgroundError(undefined);
      setSelectedBackground(file);
      setBackgroundPreviewUrl(URL.createObjectURL(file));
    }
  };

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "email",
      label: "Email",
      type: "text",
      validationError: formError?.validationErrors?.email?.message,
    },
    {
      name: "logo",
      label: "Logotipo",
      type: "logo",
      accept: LOGO_UPLOAD_CONFIG.ACCEPTED_TYPES.join(","),
      helperText: `Tamanho máximo: ${
        LOGO_UPLOAD_CONFIG.MAX_FILE_SIZE / 1000
      }KB. Dimensões máximas: ${LOGO_UPLOAD_CONFIG.MAX_DIMENSIONS.width}x${
        LOGO_UPLOAD_CONFIG.MAX_DIMENSIONS.height
      }px`,
      error: !!logoError,
      validationError: logoError,
      previewUrl: logoPreviewUrl,
      value: formData.logoUrl,
      onChange: handleLogoChange,
    },
    {
      name: "background",
      label: "Background",
      type: "logo",
      accept: BACKGROUND_UPLOAD_CONFIG.ACCEPTED_TYPES.join(","),
      helperText: `Tamanho máximo: ${
        BACKGROUND_UPLOAD_CONFIG.MAX_FILE_SIZE / 1000
      }KB. Dimensões máximas: ${
        BACKGROUND_UPLOAD_CONFIG.MAX_DIMENSIONS.width
      }x${BACKGROUND_UPLOAD_CONFIG.MAX_DIMENSIONS.height}px`,
      error: !!backgroundError,
      validationError: backgroundError,
      previewUrl: backgroundPreviewUrl,
      value: formData.backgroundUrl,
      onChange: handleBackgroundChange,
    },
  ];

  const handleSubmit = async (data: Partial<AffiliateOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    if (logoError) {
      notification.showError("Corrija os erros do logo antes de continuar");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      const finalData = {
        ...data,
        data: localFields,
        logo: selectedLogo || undefined,
        background: selectedBackground || undefined,
      };

      const mappedData = AffiliateMapper.toDTO(finalData);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateAffiliate({ id, data: mappedData }))
        : await dispatch(createAffiliate(mappedData));

      if (
        updateAffiliate.rejected.match(resultAction) ||
        createAffiliate.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/affiliates");
    } catch (error) {
      console.error("❌ AffiliateForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  if (isInitialLoad && loading) {
    return <FormSkeleton fields={2} additionalFields={true} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        {id ? "Editar Afiliado" : "Novo Afiliado"}
      </Typography>
      <DataForm
        data={formData}
        fields={fields}
        onSubmit={handleSubmit}
        basePath="affiliates"
        title={id ? "Editar Afiliado" : "Novo Afiliado"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
    </Box>
  );
};

interface DataItemFormProps {
  field: AffiliateFieldData;
  index: number;
  id: string;
  onUpdate: (index: number, data: AffiliateFieldData) => void;
  onRemove: (index: number) => void;
  error?: any;
}

const DataItemForm: React.FC<DataItemFormProps> = ({
  field,
  index,
  id,
  onUpdate,
  onRemove,
  error,
}) => {
  const handleFieldChange = (name: string, value: any) => {
    onUpdate(index, {
      ...field,
      [name]: value,
    });
  };

  const baseFields: Field[] = [
    {
      name: "name",
      label: "Nome do Campo",
      type: "text",
      required: true,
    },
    {
      name: "email",
      label: "Email",
      type: "text",
      required: true,
    },
  ];

  return (
    <DataForm
      data={field}
      fields={baseFields}
      onSubmit={async () => Promise.resolve()}
      basePath=""
      title=""
      hideButtons
      onChange={(name, value) => handleFieldChange(name, value)}
    />
  );
};

export default AffiliateForm;
